import React, { useEffect, useState, useMemo } from 'react'
import { Card, Container, Row, Col, Table, Button, Badge, Spinner, Form, InputGroup, Dropdown } from 'react-bootstrap';
import { ReportingService } from '../../../service/reporting-service';

const formatCurrency = (val) => {
    if (val === null || val === undefined) return '€0';
    return `€${Number(val).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ROWS_PER_PAGE = 10;

const Reportistica = () => {
    const [revenueData, setRevenueData] = useState([]);
    const [activityData, setActivityData] = useState([]);
    const [loadingRevenue, setLoadingRevenue] = useState(true);
    const [loadingActivity, setLoadingActivity] = useState(true);

    // Search & sort for revenue table
    const [revenueSearch, setRevenueSearch] = useState('');
    const [revenueSortField, setRevenueSortField] = useState('name');
    const [revenueSortDir, setRevenueSortDir] = useState('asc');
    const [revenuePage, setRevenuePage] = useState(1);

    // Search & sort for activity table
    const [activitySearch, setActivitySearch] = useState('');
    const [activitySortField, setActivitySortField] = useState('name');
    const [activitySortDir, setActivitySortDir] = useState('asc');
    const [activityFilter, setActivityFilter] = useState('all'); // 'all', 'online', 'offline'
    const [activityPage, setActivityPage] = useState(1);

    // Date filters for revenue report
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

    useEffect(() => {
        loadActivityReport();
    }, []);

    useEffect(() => {
        loadRevenueReport();
    }, [startDate, endDate]);

    const loadRevenueReport = async () => {
        setLoadingRevenue(true);
        try {
            const service = new ReportingService();
            const response = await service.getAgentRevenueReport({
                start_date: startDate,
                end_date: endDate
            });
            setRevenueData(response.agents || []);
        } catch (error) {
            console.error("Error loading revenue report", error);
        } finally {
            setLoadingRevenue(false);
        }
    };

    const loadActivityReport = async () => {
        setLoadingActivity(true);
        try {
            const service = new ReportingService();
            const response = await service.getAgentActivityReport();
            setActivityData(response.agents || []);
        } catch (error) {
            console.error("Error loading activity report", error);
        } finally {
            setLoadingActivity(false);
        }
    };

    const getTargetProgress = (agent) => {
        if (!agent.target || agent.target === 0) return null;
        const progress = (agent.liquidated_revenue / agent.target) * 100;
        return Math.min(progress, 100);
    };

    const getTargetColor = (progress) => {
        if (progress === null) return 'secondary';
        if (progress >= 100) return 'success';
        if (progress >= 60) return 'warning';
        return 'danger';
    };

    const formatDateTime = (isoStr) => {
        if (!isoStr) return '-';
        return new Date(isoStr).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // --- Filtered & Sorted Revenue Data ---
    const filteredRevenueData = useMemo(() => {
        let data = [...revenueData];
        if (revenueSearch.trim()) {
            const q = revenueSearch.toLowerCase();
            data = data.filter(a =>
                `${a.name} ${a.surname}`.toLowerCase().includes(q) ||
                (a.email || '').toLowerCase().includes(q) ||
                (a.agency_name || '').toLowerCase().includes(q)
            );
        }
        data.sort((a, b) => {
            let valA, valB;
            switch (revenueSortField) {
                case 'name': valA = `${a.name} ${a.surname}`.toLowerCase(); valB = `${b.name} ${b.surname}`.toLowerCase(); break;
                case 'total_revenue': valA = a.total_revenue; valB = b.total_revenue; break;
                case 'liquidated_revenue': valA = a.liquidated_revenue; valB = b.liquidated_revenue; break;
                case 'total_provvigioni': valA = a.total_provvigioni; valB = b.total_provvigioni; break;
                case 'liquidated_provvigioni': valA = a.liquidated_provvigioni; valB = b.liquidated_provvigioni; break;
                case 'target': valA = a.target; valB = b.target; break;
                default: valA = `${a.name}`; valB = `${b.name}`;
            }
            if (typeof valA === 'string') return revenueSortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            return revenueSortDir === 'asc' ? valA - valB : valB - valA;
        });
        return data;
    }, [revenueData, revenueSearch, revenueSortField, revenueSortDir]);

    const revenueTotalPages = Math.max(1, Math.ceil(filteredRevenueData.length / ROWS_PER_PAGE));
    const paginatedRevenueData = filteredRevenueData.slice((revenuePage - 1) * ROWS_PER_PAGE, revenuePage * ROWS_PER_PAGE);

    // Reset page when search changes
    useEffect(() => { setRevenuePage(1); }, [revenueSearch, revenueSortField, revenueSortDir]);

    // --- Filtered & Sorted Activity Data ---
    const filteredActivityData = useMemo(() => {
        let data = [...activityData];
        if (activityFilter === 'online') data = data.filter(a => a.is_online);
        if (activityFilter === 'offline') data = data.filter(a => !a.is_online);
        if (activitySearch.trim()) {
            const q = activitySearch.toLowerCase();
            data = data.filter(a =>
                `${a.name} ${a.surname}`.toLowerCase().includes(q) ||
                (a.email || '').toLowerCase().includes(q) ||
                (a.agency_name || '').toLowerCase().includes(q)
            );
        }
        data.sort((a, b) => {
            let valA, valB;
            switch (activitySortField) {
                case 'name': valA = `${a.name} ${a.surname}`.toLowerCase(); valB = `${b.name} ${b.surname}`.toLowerCase(); break;
                case 'last_login': valA = a.last_login || ''; valB = b.last_login || ''; break;
                case 'last_activity': valA = a.last_activity || ''; valB = b.last_activity || ''; break;
                case 'status': valA = a.is_online ? 1 : 0; valB = b.is_online ? 1 : 0; break;
                default: valA = `${a.name}`; valB = `${b.name}`;
            }
            if (typeof valA === 'string') return activitySortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            return activitySortDir === 'asc' ? valA - valB : valB - valA;
        });
        return data;
    }, [activityData, activitySearch, activitySortField, activitySortDir, activityFilter]);

    const activityTotalPages = Math.max(1, Math.ceil(filteredActivityData.length / ROWS_PER_PAGE));
    const paginatedActivityData = filteredActivityData.slice((activityPage - 1) * ROWS_PER_PAGE, activityPage * ROWS_PER_PAGE);

    useEffect(() => { setActivityPage(1); }, [activitySearch, activitySortField, activitySortDir, activityFilter]);

    // Totals for revenue table (use filtered data)
    const revenueTotals = filteredRevenueData.reduce((acc, agent) => ({
        total_revenue: acc.total_revenue + agent.total_revenue,
        liquidated_revenue: acc.liquidated_revenue + agent.liquidated_revenue,
        total_provvigioni: acc.total_provvigioni + agent.total_provvigioni,
        liquidated_provvigioni: acc.liquidated_provvigioni + agent.liquidated_provvigioni,
        target: acc.target + agent.target,
    }), { total_revenue: 0, liquidated_revenue: 0, total_provvigioni: 0, liquidated_provvigioni: 0, target: 0 });

    const SortableHeader = ({ label, field, currentField, currentDir, onSort, className }) => (
        <th
            className={className}
            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
            onClick={() => onSort(field, currentField === field && currentDir === 'asc' ? 'desc' : 'asc')}
        >
            {label}
            {currentField === field && (
                <i className={`ri-arrow-${currentDir === 'asc' ? 'up' : 'down'}-s-line ml-1`} style={{ fontSize: '12px' }}></i>
            )}
        </th>
    );

    const onlineCount = activityData.filter(a => a.is_online).length;
    const offlineCount = activityData.filter(a => !a.is_online).length;

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col className='d-flex flex-row justify-content-between align-items-center'>
                    <h2 className="font-weight-bold mb-0">
                        Reportistica Agenti
                    </h2>
                    <Button variant="primary" onClick={() => { loadRevenueReport(); loadActivityReport(); }} className="px-4" style={{ borderRadius: '8px' }}>
                        <i className="ri-refresh-line mr-1"></i> Aggiorna Tutto
                    </Button>
                </Col>
            </Row>

            {/* Summary stat cards */}
            <Row className="mb-4">
                <Col md={3} sm={6} className="mb-3">
                    <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="d-flex align-items-center justify-content-center rounded" style={{ width: '48px', height: '48px', backgroundColor: '#F59E0B' }}>
                                    <i className="ri-money-euro-circle-line" style={{ fontSize: '24px', color: '#fff' }}></i>
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-muted mb-1 small">Ricavi Totali</p>
                                <h3 className="mb-0 font-weight-bold" style={{ color: '#F59E0B' }}>{formatCurrency(revenueTotals.total_revenue)}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="d-flex align-items-center justify-content-center rounded" style={{ width: '48px', height: '48px', backgroundColor: '#8B5CF6' }}>
                                    <i className="ri-hand-coin-line" style={{ fontSize: '24px', color: '#fff' }}></i>
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-muted mb-1 small">Provvigioni Totali</p>
                                <h3 className="mb-0 font-weight-bold" style={{ color: '#8B5CF6' }}>{formatCurrency(revenueTotals.total_provvigioni)}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="d-flex align-items-center justify-content-center rounded" style={{ width: '48px', height: '48px', backgroundColor: '#3B82F6' }}>
                                    <i className="ri-team-line" style={{ fontSize: '24px', color: '#fff' }}></i>
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-muted mb-1 small">Agenti Totali</p>
                                <h3 className="mb-0 font-weight-bold" style={{ color: '#3B82F6' }}>{activityData.length}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="d-flex align-items-center justify-content-center rounded" style={{ width: '48px', height: '48px', backgroundColor: '#10B981' }}>
                                    <i className="ri-user-follow-line" style={{ fontSize: '24px', color: '#fff' }}></i>
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-muted mb-1 small">Agenti Online</p>
                                <h3 className="mb-0 font-weight-bold" style={{ color: '#10B981' }}>{onlineCount}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Side-by-side tables */}
            <Row>
                {/* Revenue / Commission Report */}
                <Col lg={6} className="mb-4">
                    <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <Card.Header className="bg-white border-bottom" style={{ borderRadius: '12px 12px 0 0' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">
                                    <i className="ri-money-euro-circle-line mr-2" style={{ color: '#F59E0B' }}></i>
                                    Ricavi e Provvigioni
                                </h5>
                            </div>
                            <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                                <InputGroup size="sm" style={{ maxWidth: '220px' }}>
                                    <InputGroup.Prepend>
                                        <InputGroup.Text style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                                            <i className="ri-search-line" style={{ fontSize: '14px' }}></i>
                                        </InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                        placeholder="Cerca agente..."
                                        value={revenueSearch}
                                        onChange={(e) => setRevenueSearch(e.target.value)}
                                        style={{ border: '1px solid #dee2e6' }}
                                    />
                                </InputGroup>
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    size="sm"
                                    style={{ width: '140px' }}
                                />
                                <span className="text-muted">-</span>
                                <Form.Control
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    size="sm"
                                    style={{ width: '140px' }}
                                />
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0" style={{ maxHeight: '520px', overflowY: 'auto' }}>
                            {loadingRevenue ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted small">Caricamento report ricavi...</p>
                                </div>
                            ) : filteredRevenueData.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="ri-inbox-line" style={{ fontSize: '40px', color: '#dee2e6' }}></i>
                                    <p className="text-muted mt-2 small">Nessun dato disponibile</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover size="sm" className="mb-0">
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                            <tr>
                                                <SortableHeader label="Agente" field="name" currentField={revenueSortField} currentDir={revenueSortDir} onSort={(f, d) => { setRevenueSortField(f); setRevenueSortDir(d); }} />
                                                <SortableHeader label="Ricavi" field="total_revenue" currentField={revenueSortField} currentDir={revenueSortDir} onSort={(f, d) => { setRevenueSortField(f); setRevenueSortDir(d); }} className="text-right" />
                                                <SortableHeader label="Liquidati" field="liquidated_revenue" currentField={revenueSortField} currentDir={revenueSortDir} onSort={(f, d) => { setRevenueSortField(f); setRevenueSortDir(d); }} className="text-right" />
                                                <SortableHeader label="Provvigioni" field="total_provvigioni" currentField={revenueSortField} currentDir={revenueSortDir} onSort={(f, d) => { setRevenueSortField(f); setRevenueSortDir(d); }} className="text-right" />
                                                <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Target</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedRevenueData.map((agent, idx) => {
                                                const progress = getTargetProgress(agent);
                                                const color = getTargetColor(progress);
                                                return (
                                                    <tr key={agent.email} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#f9fafb' }}>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            <strong className="small">{agent.name} {agent.surname}</strong>
                                                            <br />
                                                            <span className="text-muted" style={{ fontSize: '11px' }}>{agent.agency_name || '-'}</span>
                                                        </td>
                                                        <td className="text-right small">{formatCurrency(agent.total_revenue)}</td>
                                                        <td className="text-right small">
                                                            <strong>{formatCurrency(agent.liquidated_revenue)}</strong>
                                                        </td>
                                                        <td className="text-right small">{formatCurrency(agent.total_provvigioni)}</td>
                                                        <td className="text-center" style={{ minWidth: '100px' }}>
                                                            {progress !== null ? (
                                                                <div>
                                                                    <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
                                                                        <div 
                                                                            className={`progress-bar bg-${color}`}
                                                                            style={{ width: `${progress}%`, borderRadius: '3px' }}
                                                                        ></div>
                                                                    </div>
                                                                    <span style={{ fontSize: '10px' }} className={`text-${color}`}>
                                                                        {progress.toFixed(0)}%
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted" style={{ fontSize: '10px' }}>-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                        {filteredRevenueData.length > 0 && (
                            <Card.Footer className="bg-white border-top d-flex justify-content-between align-items-center py-2" style={{ borderRadius: '0 0 12px 12px' }}>
                                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                                    <small className="text-muted">
                                        {filteredRevenueData.length} agenti
                                        {revenueSearch && ` (filtrati)`}
                                    </small>
                                    <Badge style={{ backgroundColor: '#F59E0B', color: '#fff', fontSize: '11px' }} className="px-2 py-1">
                                        Tot: {formatCurrency(revenueTotals.liquidated_revenue)}
                                    </Badge>
                                </div>
                                {revenueTotalPages > 1 && (
                                    <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                        <Button variant="outline-secondary" size="sm" disabled={revenuePage <= 1} onClick={() => setRevenuePage(p => p - 1)} style={{ padding: '2px 8px', fontSize: '12px' }}>
                                            <i className="ri-arrow-left-s-line"></i>
                                        </Button>
                                        <small className="text-muted mx-1">{revenuePage}/{revenueTotalPages}</small>
                                        <Button variant="outline-secondary" size="sm" disabled={revenuePage >= revenueTotalPages} onClick={() => setRevenuePage(p => p + 1)} style={{ padding: '2px 8px', fontSize: '12px' }}>
                                            <i className="ri-arrow-right-s-line"></i>
                                        </Button>
                                    </div>
                                )}
                            </Card.Footer>
                        )}
                    </Card>
                </Col>

                {/* Activity Report */}
                <Col lg={6} className="mb-4">
                    <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <Card.Header className="bg-white border-bottom" style={{ borderRadius: '12px 12px 0 0' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">
                                    <i className="ri-user-heart-line mr-2" style={{ color: '#3B82F6' }}></i>
                                    Attività Agenti
                                </h5>
                                <Button variant="outline-primary" size="sm" onClick={loadActivityReport} style={{ borderRadius: '6px' }}>
                                    <i className="ri-refresh-line"></i>
                                </Button>
                            </div>
                            <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                                <InputGroup size="sm" style={{ maxWidth: '220px' }}>
                                    <InputGroup.Prepend>
                                        <InputGroup.Text style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                                            <i className="ri-search-line" style={{ fontSize: '14px' }}></i>
                                        </InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                        placeholder="Cerca agente..."
                                        value={activitySearch}
                                        onChange={(e) => setActivitySearch(e.target.value)}
                                        style={{ border: '1px solid #dee2e6' }}
                                    />
                                </InputGroup>
                                <div className="d-flex" style={{ gap: '4px' }}>
                                    {[
                                        { key: 'all', label: 'Tutti' },
                                        { key: 'online', label: 'Online' },
                                        { key: 'offline', label: 'Offline' }
                                    ].map(opt => (
                                        <Badge
                                            key={opt.key}
                                            variant={activityFilter === opt.key ? 'primary' : 'light'}
                                            className="px-2 py-1"
                                            style={{
                                                cursor: 'pointer',
                                                fontSize: '0.7rem',
                                                fontWeight: activityFilter === opt.key ? '600' : '400',
                                                border: activityFilter === opt.key ? 'none' : '1px solid #dee2e6'
                                            }}
                                            onClick={() => setActivityFilter(opt.key)}
                                        >
                                            {opt.label}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0" style={{ maxHeight: '520px', overflowY: 'auto' }}>
                            {loadingActivity ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted small">Caricamento attività agenti...</p>
                                </div>
                            ) : filteredActivityData.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="ri-inbox-line" style={{ fontSize: '40px', color: '#dee2e6' }}></i>
                                    <p className="text-muted mt-2 small">Nessun dato disponibile</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover size="sm" className="mb-0">
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                            <tr>
                                                <SortableHeader label="Agente" field="name" currentField={activitySortField} currentDir={activitySortDir} onSort={(f, d) => { setActivitySortField(f); setActivitySortDir(d); }} />
                                                <SortableHeader label="Stato" field="status" currentField={activitySortField} currentDir={activitySortDir} onSort={(f, d) => { setActivitySortField(f); setActivitySortDir(d); }} className="text-center" />
                                                <SortableHeader label="Ultimo Accesso" field="last_login" currentField={activitySortField} currentDir={activitySortDir} onSort={(f, d) => { setActivitySortField(f); setActivitySortDir(d); }} />
                                                <SortableHeader label="Ultima Attività" field="last_activity" currentField={activitySortField} currentDir={activitySortDir} onSort={(f, d) => { setActivitySortField(f); setActivitySortDir(d); }} />
                                                <th style={{ whiteSpace: 'nowrap' }}>Sessione</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedActivityData.map((agent, idx) => (
                                                <tr key={agent.email} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#f9fafb' }}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <strong className="small">{agent.name} {agent.surname}</strong>
                                                        <br />
                                                        <span className="text-muted" style={{ fontSize: '11px' }}>{agent.agency_name || '-'}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge
                                                            variant={agent.is_online ? 'success' : 'secondary'}
                                                            className="px-2 py-1"
                                                            style={{ fontSize: '11px' }}
                                                        >
                                                            <i className="ri-checkbox-blank-circle-fill mr-1" style={{ fontSize: '6px' }}></i>
                                                            {agent.is_online ? 'Online' : 'Offline'}
                                                        </Badge>
                                                    </td>
                                                    <td className="small">{formatDateTime(agent.last_login)}</td>
                                                    <td className="small">{formatDateTime(agent.last_activity)}</td>
                                                    <td>
                                                        {agent.duration_online ? (
                                                            <Badge variant="info" className="px-2 py-1" style={{ fontSize: '11px' }}>
                                                                <i className="ri-time-line mr-1"></i>
                                                                {agent.duration_online}
                                                            </Badge>
                                                        ) : <span className="text-muted small">-</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                        {filteredActivityData.length > 0 && (
                            <Card.Footer className="bg-white border-top d-flex justify-content-between align-items-center py-2" style={{ borderRadius: '0 0 12px 12px' }}>
                                <small className="text-muted">
                                    {filteredActivityData.length} agenti
                                    {(activitySearch || activityFilter !== 'all') && ` (filtrati)`}
                                </small>
                                {activityTotalPages > 1 && (
                                    <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                        <Button variant="outline-secondary" size="sm" disabled={activityPage <= 1} onClick={() => setActivityPage(p => p - 1)} style={{ padding: '2px 8px', fontSize: '12px' }}>
                                            <i className="ri-arrow-left-s-line"></i>
                                        </Button>
                                        <small className="text-muted mx-1">{activityPage}/{activityTotalPages}</small>
                                        <Button variant="outline-secondary" size="sm" disabled={activityPage >= activityTotalPages} onClick={() => setActivityPage(p => p + 1)} style={{ padding: '2px 8px', fontSize: '12px' }}>
                                            <i className="ri-arrow-right-s-line"></i>
                                        </Button>
                                    </div>
                                )}
                            </Card.Footer>
                        )}
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Reportistica;
